import { useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Layout } from "../components/Layout"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/Button"
import { Skeleton } from "@/components/Skeleton/Skeleton"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/Dialog/Dialog"
import { ConfirmDialog } from "@/components/Dialog/ConfirmDialog"
import { Input } from "@/components/Form/Input"
import { useToast } from "@/components/Toast"
import {
  useAffiliateDetail,
  useAffiliateCredits,
  useMarkAffiliatePaid,
  useSendReferralEmail,
  useAffiliateRefCodes,
  useAddAffiliateRefCode,
  useUpdateAffiliateRefCode,
  type AffiliateRefCode,
} from "@/hooks/useAffiliateDetail"
import { useUpdateAffiliate } from "@/hooks/useAffiliates"
import { AffiliateProgramTerms } from "@/components/AffiliateProgramTerms"
import { AffiliateStatements } from "@/components/AffiliateStatements"
import { APIError } from "@/api"
import {
  ArrowLeftIcon,
  ClipboardDocumentIcon,
  CurrencyDollarIcon,
  GiftIcon,
  PaperAirplaneIcon,
  PencilIcon,
  PlusIcon,
} from "@heroicons/react/24/outline"

const formatCents = (cents: number) => `$${(cents / 100).toFixed(2)}`

function Stat({
  label,
  value,
  tone = "neutral",
}: {
  label: string
  value: string
  tone?: "good" | "bad" | "neutral"
}) {
  const valueColor =
    tone === "good" ? "text-green-400" : tone === "bad" ? "text-amber-400" : "text-primary"
  return (
    <div className="rounded-md border border-border bg-background/40 p-3">
      <div className="text-xs text-secondary">{label}</div>
      <div className={`text-xl font-semibold ${valueColor}`}>{value}</div>
    </div>
  )
}

export function AffiliateDetail() {
  const { affiliateId } = useParams<{ affiliateId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const publicId = affiliateId || ""

  const { data, isLoading, error, refetch } = useAffiliateDetail(publicId)
  const { data: creditsData, refetch: refetchCredits } = useAffiliateCredits(publicId, {
    status: "pending",
  })
  const { data: refCodesData, refetch: refetchRefCodes } = useAffiliateRefCodes(publicId)
  const markPaid = useMarkAffiliatePaid()
  const sendReferralEmail = useSendReferralEmail()
  const updateAffiliate = useUpdateAffiliate()
  const addRefCode = useAddAffiliateRefCode()
  const updateRefCode = useUpdateAffiliateRefCode()

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [showMarkPaidDialog, setShowMarkPaidDialog] = useState(false)
  const [payoutForm, setPayoutForm] = useState({
    method: "check" as "check" | "ach" | "paypal" | "wire" | "other",
    reference: "",
    adminNotes: "",
  })
  const [showSendEmailDialog, setShowSendEmailDialog] = useState(false)
  const [sendEmailForm, setSendEmailForm] = useState({ recipientEmail: "", personalNote: "" })
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [editForm, setEditForm] = useState({
    name: "",
    contactEmail: "",
    payoutEmail: "",
    commissionPercent: "10",
    status: "active" as "active" | "inactive",
    adminNotes: "",
  })
  const [showAddLinkForm, setShowAddLinkForm] = useState(false)
  const [addLinkForm, setAddLinkForm] = useState({ refCode: "", label: "" })
  const [editingLinkId, setEditingLinkId] = useState<string | null>(null)
  const [editLinkLabel, setEditLinkLabel] = useState("")
  const [confirmDeactivateLink, setConfirmDeactivateLink] = useState<AffiliateRefCode | null>(null)

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  if (isLoading || !data) {
    return (
      <Layout>
        <div className="space-y-4">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Layout>
    )
  }

  const { affiliate, referredUsers, referredUsersTotalCount, totals } = data
  const selectedCredits = (creditsData?.credits || []).filter((credit) =>
    selected.has(credit.public_id)
  )
  const selectedAmountCents = selectedCredits.reduce(
    (sum, credit) => sum + credit.commission_amount_cents,
    0
  )

  const toggleSelected = (publicId: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(publicId)) next.delete(publicId)
      else next.add(publicId)
      return next
    })
  }

  const openEditDialog = () => {
    setEditForm({
      name: affiliate.name,
      contactEmail: affiliate.contact_email,
      payoutEmail: affiliate.payout_email,
      commissionPercent: String(affiliate.commission_percent),
      status: affiliate.status,
      adminNotes: affiliate.admin_notes || "",
    })
    setShowEditDialog(true)
  }

  const onEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await updateAffiliate.mutateAsync({
        publicId,
        name: editForm.name.trim(),
        contactEmail: editForm.contactEmail.trim(),
        payoutEmail: editForm.payoutEmail.trim(),
        commissionPercent: Number(editForm.commissionPercent),
        status: editForm.status,
        adminNotes: editForm.adminNotes.trim(),
      })
      toast({ title: "Affiliate updated" })
      setShowEditDialog(false)
      refetch()
    } catch (e) {
      toast({
        title: "Error updating affiliate",
        description: e instanceof APIError ? e.originalError.message : "Request failed",
        variant: "destructive",
      })
    }
  }

  const onAddLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!addLinkForm.refCode.trim()) return
    try {
      await addRefCode.mutateAsync({
        publicId,
        refCode: addLinkForm.refCode.trim(),
        label: addLinkForm.label.trim() || undefined,
      })
      toast({ title: "Link added", description: addLinkForm.refCode.trim().toLowerCase() })
      setAddLinkForm({ refCode: "", label: "" })
      setShowAddLinkForm(false)
      refetchRefCodes()
    } catch (e) {
      toast({
        title: "Error adding link",
        description: e instanceof APIError ? e.originalError.message : "Request failed",
        variant: "destructive",
      })
    }
  }

  const toggleLinkStatus = async (refCodePublicId: string, current: "active" | "inactive") => {
    try {
      await updateRefCode.mutateAsync({
        publicId,
        refCodePublicId,
        status: current === "active" ? "inactive" : "active",
      })
      refetchRefCodes()
    } catch (e) {
      toast({
        title: "Error updating link",
        description: e instanceof APIError ? e.originalError.message : "Request failed",
        variant: "destructive",
      })
    }
  }

  const startEditLinkLabel = (refCodePublicId: string, currentLabel: string | null) => {
    setEditingLinkId(refCodePublicId)
    setEditLinkLabel(currentLabel || "")
  }

  const saveLinkLabel = async (refCodePublicId: string) => {
    try {
      await updateRefCode.mutateAsync({
        publicId,
        refCodePublicId,
        label: editLinkLabel.trim() || null,
      })
      setEditingLinkId(null)
      refetchRefCodes()
    } catch (e) {
      toast({
        title: "Error updating link",
        description: e instanceof APIError ? e.originalError.message : "Request failed",
        variant: "destructive",
      })
    }
  }

  const copyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      toast({ title: "Link copied", description: "Referral link copied to clipboard." })
    } catch (_error) {
      toast({
        title: "Copy failed",
        description: "Unable to copy this link from the browser.",
        variant: "destructive",
      })
    }
  }

  const onMarkPaidSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (selected.size === 0) return
    try {
      await markPaid.mutateAsync({
        publicId,
        creditPublicIds: Array.from(selected),
        method: payoutForm.method,
        reference: payoutForm.reference.trim() || undefined,
        adminNotes: payoutForm.adminNotes.trim() || undefined,
      })
      toast({
        title: "Marked paid",
        description: `${formatCents(selectedAmountCents)} across ${selected.size} credit(s)`,
      })
      setSelected(new Set())
      setShowMarkPaidDialog(false)
      setPayoutForm({ method: "check", reference: "", adminNotes: "" })
      refetch()
      refetchCredits()
    } catch (e) {
      toast({
        title: "Error marking paid",
        description: e instanceof APIError ? e.originalError.message : "Request failed",
        variant: "destructive",
      })
    }
  }

  const onSendEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!sendEmailForm.recipientEmail.trim()) return
    try {
      await sendReferralEmail.mutateAsync({
        publicId,
        recipientEmail: sendEmailForm.recipientEmail.trim(),
        personalNote: sendEmailForm.personalNote.trim() || undefined,
      })
      toast({
        title: "Referral email sent",
        description: sendEmailForm.recipientEmail.trim(),
      })
      setSendEmailForm({ recipientEmail: "", personalNote: "" })
      setShowSendEmailDialog(false)
    } catch (e) {
      toast({
        title: "Error sending email",
        description: e instanceof APIError ? e.originalError.message : "Request failed",
        variant: "destructive",
      })
    }
  }

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader
          title={
            <span className="flex items-center gap-2">
              <GiftIcon className="h-6 w-6 text-secondary" />
              {affiliate.name}
            </span>
          }
          description={
            <span>
              Ref code:{" "}
              <code className="bg-background/40 border border-border rounded px-1.5 py-0.5">
                {affiliate.ref_code}
              </code>{" "}
              · {affiliate.commission_percent}%{" "}
              {affiliate.commission_model === "first_year_net"
                ? `of net revenue for ${affiliate.commission_window_months} months`
                : "of first payment"}{" "}
              · {affiliate.status}
            </span>
          }
          actions={
            <div className="flex gap-2">
              <Button variant="outline" onClick={openEditDialog}>
                <PencilIcon className="h-4 w-4" />
                Edit
              </Button>
              <Button variant="outline" onClick={() => navigate("/affiliates")}>
                <ArrowLeftIcon className="h-4 w-4" />
                Back to Affiliates
              </Button>
            </div>
          }
        />

        <section className="rounded-lg border border-border bg-surface p-5 space-y-3">
          <h2 className="text-lg font-semibold text-primary">Referral link</h2>
          <div className="flex flex-col gap-2 sm:flex-row">
            <p className="min-w-0 flex-1 break-all rounded-md border border-border bg-background/40 px-3 py-2 font-mono text-xs text-secondary">
              {affiliate.referralUrl}
            </p>
            <Button
              variant="outline"
              size="sm"
              disabled={affiliate.status !== "active"}
              onClick={() => void copyLink(affiliate.referralUrl)}
            >
              <ClipboardDocumentIcon className="h-4 w-4" />
              Copy
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={affiliate.status !== "active"}
              onClick={() => setShowSendEmailDialog(true)}
            >
              <PaperAirplaneIcon className="h-4 w-4" />
              Send by email
            </Button>
          </div>
          {affiliate.status !== "active" && (
            <p className="text-xs text-secondary">
              This affiliate is inactive, signups through this link would not be attributed.
              Reactivate it before sending or copying the link.
            </p>
          )}

          <div className="border-t border-border pt-3 space-y-3">
            {!refCodesData?.refCodes.length && !showAddLinkForm ? (
              <p className="text-xs text-secondary">
                No additional link variants yet, add one to track a separate channel (e.g.
                Instagram bio, email signature) that credits this same affiliate.
              </p>
            ) : (
              <ul className="space-y-2">
                {refCodesData?.refCodes.map((link) => (
                  <li
                    key={link.public_id}
                    className="flex flex-col gap-1.5 rounded-md border border-border bg-background/40 p-2.5 sm:flex-row sm:items-center sm:gap-2"
                  >
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-background/60 border border-border rounded px-1.5 py-0.5">
                          {link.ref_code}
                        </code>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            link.status === "active"
                              ? "bg-green-900/30 text-green-400"
                              : "bg-gray-800 text-secondary"
                          }`}
                        >
                          {link.status}
                        </span>
                      </div>
                      {editingLinkId === link.public_id ? (
                        <div className="flex items-center gap-1.5">
                          <input
                            autoFocus
                            className="min-w-0 flex-1 rounded border border-border bg-background px-2 py-1 text-xs text-foreground"
                            placeholder="Label (e.g. Instagram bio)"
                            value={editLinkLabel}
                            onChange={(e) => setEditLinkLabel(e.target.value)}
                          />
                          <Button size="sm" onClick={() => void saveLinkLabel(link.public_id)}>
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setEditingLinkId(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="text-xs text-secondary hover:underline"
                          onClick={() => startEditLinkLabel(link.public_id, link.label)}
                        >
                          {link.label || "Add a label..."}
                        </button>
                      )}
                    </div>
                    <div className="flex gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={link.status !== "active" || affiliate.status !== "active"}
                        onClick={() => void copyLink(link.referralUrl)}
                      >
                        <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          link.status === "active"
                            ? setConfirmDeactivateLink(link)
                            : void toggleLinkStatus(link.public_id, link.status)
                        }
                      >
                        {link.status === "active" ? "Deactivate" : "Reactivate"}
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {showAddLinkForm ? (
              <form onSubmit={onAddLinkSubmit} className="space-y-2 rounded-md border border-border p-3">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Input
                    label="Ref code"
                    placeholder="coach-mike-insta"
                    value={addLinkForm.refCode}
                    onChange={(e) => setAddLinkForm((s) => ({ ...s, refCode: e.target.value }))}
                    required
                  />
                  <Input
                    label="Label (optional)"
                    placeholder="Instagram bio"
                    value={addLinkForm.label}
                    onChange={(e) => setAddLinkForm((s) => ({ ...s, label: e.target.value }))}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setShowAddLinkForm(false)
                      setAddLinkForm({ refCode: "", label: "" })
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    size="sm"
                    disabled={addRefCode.isPending || !addLinkForm.refCode.trim()}
                  >
                    {addRefCode.isPending ? "Adding..." : "Add link"}
                  </Button>
                </div>
              </form>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setShowAddLinkForm(true)}>
                <PlusIcon className="h-4 w-4" />
                Add link...
              </Button>
            )}
          </div>
        </section>

        <AffiliateProgramTerms
          publicId={publicId}
          affiliate={affiliate}
          hasReferrals={affiliate.has_referrals}
        />

        {affiliate.commission_model === "first_year_net" && (
          <AffiliateStatements publicId={publicId} cadence={affiliate.statement_cadence} />
        )}

        <section className="rounded-lg border border-border bg-surface p-5 space-y-4">
          <h2 className="text-lg font-semibold text-primary">Commission summary</h2>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat label="Total earned" value={formatCents(totals.totalEarnedCents)} />
            <Stat label="Paid out" value={formatCents(totals.totalPaidCents)} tone="good" />
            <Stat
              label="Owed"
              value={formatCents(totals.totalOwedCents)}
              tone={totals.totalOwedCents > 0 ? "bad" : "neutral"}
            />
            <Stat label="Referred signups" value={String(referredUsersTotalCount)} />
          </div>
          <div className="text-sm text-secondary space-y-1">
            <div>Contact: {affiliate.contact_email}</div>
            <div>Payout: {affiliate.payout_email}</div>
            {affiliate.admin_notes && <div>Notes: {affiliate.admin_notes}</div>}
          </div>
        </section>

        <section className="rounded-lg border border-border bg-surface p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-primary">Pending credits</h2>
            <Button
              disabled={selected.size === 0}
              onClick={() => setShowMarkPaidDialog(true)}
            >
              <CurrencyDollarIcon className="h-4 w-4 mr-2" />
              Mark {selected.size > 0 ? formatCents(selectedAmountCents) : ""} paid
            </Button>
          </div>

          {!creditsData?.credits.length ? (
            <p className="text-sm text-secondary">No pending credits.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-secondary border-b border-border">
                  <th className="py-2 pr-2 w-8"></th>
                  <th className="py-2 pr-2">Credit</th>
                  <th className="py-2 pr-2">Stripe subscription</th>
                  <th className="py-2 pr-2">Gross</th>
                  <th className="py-2 pr-2">Commission</th>
                  <th className="py-2 pr-2">Earned</th>
                </tr>
              </thead>
              <tbody>
                {creditsData.credits.map((credit) => (
                  <tr key={credit.public_id} className="border-b border-border/50">
                    <td className="py-2 pr-2">
                      <input
                        type="checkbox"
                        checked={selected.has(credit.public_id)}
                        onChange={() => toggleSelected(credit.public_id)}
                      />
                    </td>
                    <td className="py-2 pr-2 font-mono text-xs">{credit.public_id}</td>
                    <td className="py-2 pr-2 font-mono text-xs">
                      {credit.stripe_subscription_id}
                    </td>
                    <td className="py-2 pr-2">{formatCents(credit.gross_amount_cents)}</td>
                    <td className="py-2 pr-2 font-medium text-amber-400">
                      {formatCents(credit.commission_amount_cents)}
                    </td>
                    <td className="py-2 pr-2 text-secondary">
                      {new Date(credit.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="rounded-lg border border-border bg-surface p-5 space-y-4">
          <h2 className="text-lg font-semibold text-primary">
            Referred signups ({referredUsersTotalCount})
            {referredUsersTotalCount > referredUsers.length && (
              <span className="text-sm font-normal text-secondary">
                {" "}
               , showing {referredUsers.length} most recent
              </span>
            )}
          </h2>
          {referredUsers.length === 0 ? (
            <p className="text-sm text-secondary">No signups attributed to this affiliate yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-secondary border-b border-border">
                  <th className="py-2 pr-2">Name</th>
                  <th className="py-2 pr-2">Email</th>
                  <th className="py-2 pr-2">Signed up</th>
                </tr>
              </thead>
              <tbody>
                {referredUsers.map((user) => (
                  <tr key={user.public_id} className="border-b border-border/50">
                    <td className="py-2 pr-2">{user.name}</td>
                    <td className="py-2 pr-2 text-secondary">{user.email}</td>
                    <td className="py-2 pr-2 text-secondary">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>

      <Dialog open={showMarkPaidDialog} onOpenChange={setShowMarkPaidDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark {formatCents(selectedAmountCents)} paid</DialogTitle>
            <DialogDescription>
              Records that a payment has actually gone out for the {selected.size} selected
              credit(s). This does not send money, cut the check/transfer first, then record it
              here.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onMarkPaidSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Method</label>
              <select
                value={payoutForm.method}
                onChange={(e) =>
                  setPayoutForm((s) => ({ ...s, method: e.target.value as typeof s.method }))
                }
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              >
                <option value="check">Check</option>
                <option value="ach">ACH</option>
                <option value="paypal">PayPal</option>
                <option value="wire">Wire</option>
                <option value="other">Other</option>
              </select>
            </div>
            <Input
              label="Reference (check #, transaction id, optional)"
              value={payoutForm.reference}
              onChange={(e) => setPayoutForm((s) => ({ ...s, reference: e.target.value }))}
            />
            <Input
              label="Notes (optional)"
              value={payoutForm.adminNotes}
              onChange={(e) => setPayoutForm((s) => ({ ...s, adminNotes: e.target.value }))}
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => setShowMarkPaidDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={markPaid.isPending}>
                {markPaid.isPending ? "Recording..." : "Confirm paid"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showSendEmailDialog} onOpenChange={setShowSendEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send referral link by email</DialogTitle>
            <DialogDescription>
              Emails the referral link directly to the person being referred, not to{" "}
              {affiliate.name}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSendEmailSubmit} className="space-y-4">
            <Input
              label="Recipient email"
              type="email"
              placeholder="prospect@example.com"
              value={sendEmailForm.recipientEmail}
              onChange={(e) =>
                setSendEmailForm((s) => ({ ...s, recipientEmail: e.target.value }))
              }
              required
            />
            <Input
              label="Personal note (optional)"
              placeholder="Thought you'd like this"
              value={sendEmailForm.personalNote}
              onChange={(e) => setSendEmailForm((s) => ({ ...s, personalNote: e.target.value }))}
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowSendEmailDialog(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={sendReferralEmail.isPending || !sendEmailForm.recipientEmail.trim()}
              >
                {sendReferralEmail.isPending ? "Sending..." : "Send email"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit affiliate</DialogTitle>
            <DialogDescription>
              Ref code (
              <code className="bg-background/40 border border-border rounded px-1 py-0.5">
                {affiliate.ref_code}
              </code>
              ) isn't editable here, it's part of an already-distributed link. Add another link
              variant below instead.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onEditSubmit} className="space-y-4">
            <Input
              label="Name"
              value={editForm.name}
              onChange={(e) => setEditForm((s) => ({ ...s, name: e.target.value }))}
              required
            />
            <Input
              label="Contact email"
              type="email"
              value={editForm.contactEmail}
              onChange={(e) => setEditForm((s) => ({ ...s, contactEmail: e.target.value }))}
              required
            />
            <Input
              label="Payout email"
              type="email"
              value={editForm.payoutEmail}
              onChange={(e) => setEditForm((s) => ({ ...s, payoutEmail: e.target.value }))}
              required
            />
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Commission %"
                type="number"
                min={0}
                max={100}
                value={editForm.commissionPercent}
                onChange={(e) =>
                  setEditForm((s) => ({ ...s, commissionPercent: e.target.value }))
                }
                disabled={affiliate.has_referrals && affiliate.commission_model === "first_year_net"}
                helperText={
                  affiliate.has_referrals && affiliate.commission_model === "first_year_net"
                    ? "Locked: someone has already signed up through this affiliate."
                    : undefined
                }
                required
              />
              <div>
                <label className="block text-sm font-medium mb-2">Status</label>
                <select
                  value={editForm.status}
                  onChange={(e) =>
                    setEditForm((s) => ({ ...s, status: e.target.value as "active" | "inactive" }))
                  }
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            <Input
              label="Admin notes (optional)"
              value={editForm.adminNotes}
              onChange={(e) => setEditForm((s) => ({ ...s, adminNotes: e.target.value }))}
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  updateAffiliate.isPending ||
                  !editForm.name.trim() ||
                  !editForm.contactEmail.trim() ||
                  !editForm.payoutEmail.trim()
                }
              >
                {updateAffiliate.isPending ? "Saving..." : "Save changes"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmDeactivateLink !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmDeactivateLink(null)
        }}
        title="Deactivate this link?"
        description={`"${confirmDeactivateLink?.ref_code}" will immediately stop attributing signups, anyone who already has this link will land on signup with no referral credited until it's reactivated.`}
        confirmLabel="Deactivate"
        confirmVariant="destructive"
        onConfirm={() => {
          if (confirmDeactivateLink) void toggleLinkStatus(confirmDeactivateLink.public_id, "active")
        }}
      />
    </Layout>
  )
}
